import type { FC, JSX } from "react";
import { useState } from "react";

import Fuse from "fuse.js";

import { AutocompleteWithItems, DotsLoader, ListItemUser, UserChipItem } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";

export interface IUser {
    FirstName: string;
    LastName: string;
    Id: number;
    Email: string;
}

interface IUsersAutocomplete {
    selectedUsers?: Array<IUser>;
    allUsers?: Array<IUser>;
    onRemoveUser?: (user: IUser) => void;
    onUserSelect?: (user: IUser) => void;
    getUsers?: (query: string) => Promise<JSX.Element[]>;
    placeholderKey?: string;
    isFocused?: boolean;
}
const USERS_TO_DISPLAY = 5;

export const UsersAutocomplete: FC<IUsersAutocomplete> = (props) => {
    const {
        selectedUsers = [],
        onRemoveUser: onRemoveUserProp,
        onUserSelect: onUserSelectProp,
        allUsers = [],
        getUsers: getUsersProp,
        placeholderKey: placeholderKeyProp,
        isFocused: isFocusedProp = false,
    } = props;
    const [isFocused, setIsFocused] = useState(isFocusedProp);

    const onRemoveUser = (user) => () => {
        onRemoveUserProp(user);
    };

    const onUserSelect = (user) => () => {
        onUserSelectProp(user);
    };

    const getUsers = async (query) => {
        let users = allUsers;
        const options = {
            keys: ["FirstName", "LastName", "Email"],
        };
        if (query) {
            const fuse = new Fuse(allUsers, options);
            users = fuse.search(query);
        }

        const filteredUsers = users
            .filter((user) => !selectedUsers.find((selectedUser) => selectedUser.Id === user.Id))
            .slice(0, USERS_TO_DISPLAY)
            .map((user) => {
                return (
                    <ListItemUser
                        key={user.Id.toString()}
                        email={user.Email}
                        name={`${user.FirstName} ${user.LastName}`}
                        onClick={onUserSelect(user)}
                    />
                );
            });
        return Promise.resolve(filteredUsers);
    };

    const getChips = () => {
        return selectedUsers.map((user) => {
            return (
                <UserChipItem
                    key={user.Id}
                    onCloseItem={onRemoveUser(user)}
                    text={`${user.FirstName} ${user.LastName}`}
                />
            );
        });
    };

    return (
        <AutocompleteWithItems
            selectedItems={getChips()}
            placeholder={translate(placeholderKeyProp ?? "keyword.groups.sharing.sharedwithusers.placeholder")}
            getListItems={getUsersProp ?? getUsers}
            loadingComponent={<DotsLoader />}
            searchIcon="add"
            isFocused={isFocused}
            debounce={400}
            onFocus={() => {
                setIsFocused(true);
            }}
            onBlur={() => {
                setIsFocused(false);
            }}
        />
    );
};
